import type {
  CrmSegment,
  CrmSegmentRule,
  CrmSegmentTemplateCategory,
  DynamicCrmThresholds,
  SegmentMemberships,
} from '@/types/crm';

/**
 * Infers the optimal WhatsApp template category based on the native rules of a Fresha segment.
 */
export function inferTemplateCategoryFromRules(
  rules?: CrmSegmentRule[],
  segmentName = '',
  _segmentId = '',
  predefinedType?: string
): CrmSegmentTemplateCategory {
  // 1. Mapeo Canónico por Enum Predefinido de Fresha (Máxima Prioridad)
  if (predefinedType) {
    switch (predefinedType) {
      case 'LOYAL_CLIENTS':
        return 'vip';
      case 'HIGH_SPENDERS':
        return 'top_spender';
      case 'LAPSED_CLIENTS':
        return 'lapsed';
      case 'FIRST_VISIT_CLIENTS':
        return 'first_visit';
      case 'NEW_CLIENTS':
        return 'new';
      case 'BIRTHDAY_CLIENTS':
        return 'birthday';
      case 'UPCOMING_APPOINTMENTS_CLIENTS':
        return 'upcoming';
      case 'RECENT_CLIENTS':
      case 'RECENT_APPOINTMENTS_CLIENTS':
        return 'recent';
      case 'ONLINE_BOOKING_CLIENTS':
        return 'online_booking';
      case 'SALES_CLIENTS':
        return 'sales';
      case 'IMPORTED_CLIENTS':
        return 'imported';
    }
  }

  // 2. Mapeo Dinámico por Reglas Métricas (Para Segmentos Personalizados Creados por el Negocio)
  if (Array.isArray(rules) && rules.length > 0) {
    const metricIds = new Set(rules.map((r) => r.metric?.id).filter(Boolean));

    if (metricIds.has('birthday')) return 'birthday';
    if (metricIds.has('total_value_of_sales')) return 'top_spender';
    if (metricIds.has('no_sales')) return 'lapsed';
    if (metricIds.has('no_appointments') && metricIds.has('any_appointments')) return 'first_visit';
    if (metricIds.has('added_date')) return 'new';
    if (metricIds.has('number_of_sales')) return 'vip';
    if (metricIds.has('any_appointments')) {
      const isFuture = rules.some((r) =>
        r.conditions?.some(
          (c) =>
            c.value?.__typename === 'Customers_SegmentMetricRuleDateIsInTheFutureValue' ||
            c.value?.condDateNextOperator === 'IS_IN_THE_NEXT'
        )
      );
      if (isFuture) return 'upcoming';
      return 'default';
    }
  }

  // 3. Fallback semántico por nombre
  const name = segmentName.toLowerCase().trim();
  if (name.includes('cumple') || name.includes('birthday')) return 'birthday';
  if (name.includes('gasta') || name.includes('spender') || name.includes('consumo')) return 'top_spender';
  if (name.includes('fiel') || name.includes('vip') || name.includes('frecuente')) return 'vip';
  if (name.includes('antiguo') || name.includes('inactiv') || name.includes('ausente') || name.includes('lapsed')) return 'lapsed';
  if (name.includes('nuevo') || name.includes('primera visita') || name.includes('1ra')) return 'new';

  return 'default';
}

/**
 * Returns a user-friendly label with icon for each template category.
 */
export function getTemplateCategoryLabel(category: CrmSegmentTemplateCategory): string {
  switch (category) {
    case 'birthday':
      return '🎂 Cumpleaños';
    case 'top_spender':
      return '💎 Mayor Consumo';
    case 'vip':
      return '⭐ Cliente Fiel';
    case 'lapsed':
      return '💤 Inactivo / Ausente';
    case 'new':
      return '👋 Nuevo Cliente';
    case 'first_visit':
      return '👋 1ra Visita';
    case 'upcoming':
      return '📅 Próxima Cita';
    case 'recent':
      return '🕒 Cliente Reciente';
    case 'online_booking':
      return '🌐 Reserva Online';
    case 'sales':
      return '🛍️ Con Ventas';
    case 'imported':
      return '📁 Importado';
    case 'default':
    default:
      return '📄 General';
  }
}

/**
 * Finds all segments matching a specified template category by rule or semantic inference.
 */
export function findSegmentsByCategory(
  segments: CrmSegment[] = [],
  category: CrmSegmentTemplateCategory | string
): CrmSegment[] {
  if (!Array.isArray(segments)) return [];
  return segments.filter(
    (s) => inferTemplateCategoryFromRules(s.rules, s.name, s.id, s.predefinedType) === category
  );
}

/**
 * Finds the first segment matching a specified template category by rule or semantic inference.
 */
export function findSegmentByCategory(
  segments: CrmSegment[] = [],
  category: CrmSegmentTemplateCategory | string
): CrmSegment | undefined {
  return findSegmentsByCategory(segments, category)[0];
}

/**
 * Checks whether a given client belongs to ANY segment matching a category resolved dynamically via rules or semantics.
 */
export function isClientInRuleCategory(
  client: { id: string; customerId?: string },
  category: CrmSegmentTemplateCategory | string,
  segments: CrmSegment[] = [],
  memberships?: SegmentMemberships
): boolean {
  if (!memberships) return false;
  const targetSegs = findSegmentsByCategory(segments, category);
  if (targetSegs.length === 0) return false;

  for (const seg of targetSegs) {
    const list = memberships[seg.id];
    if (Array.isArray(list)) {
      if (list.includes(client.id) || (client.customerId && list.includes(client.customerId))) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Dynamically extracts CRM business rule thresholds (VIP appointments, VIP sales, inactivity days)
 * directly from the native GraphQL segment rules configured in Fresha.
 */
export function extractDynamicSegmentThresholds(segments: CrmSegment[] = []): DynamicCrmThresholds {
  const thresholds: DynamicCrmThresholds = {};
  if (!Array.isArray(segments)) return thresholds;

  for (const segment of segments) {
    if (typeof segment.predefinedSettings?.minSalesCount === 'number' && thresholds.vipMinAppointments === undefined) {
      thresholds.vipMinAppointments = segment.predefinedSettings.minSalesCount;
    }

    if (!Array.isArray(segment.rules)) continue;

    for (const rule of segment.rules) {
      const metricId = rule.metric?.id;
      if (!metricId || !rule.value) continue;

      if (metricId === 'number_of_sales' && thresholds.vipMinAppointments === undefined) {
        const val = rule.value.numAtLeastValue ?? rule.value.numMoreThanValue;
        if (typeof val === 'number') {
          thresholds.vipMinAppointments = val;
        }
      } else if (metricId === 'total_value_of_sales' && thresholds.vipMinSales === undefined) {
        const val = rule.value.numMoreThanValue ?? rule.value.numAtLeastValue;
        if (typeof val === 'number') {
          thresholds.vipMinSales = val;
        }
      } else if (metricId === 'no_sales' && thresholds.inactivityDays === undefined) {
        const val = rule.value.dateLastValue;
        if (typeof val === 'number') {
          thresholds.inactivityDays = val;
        }
      }
    }
  }

  return thresholds;
}
